import { Component, DestroyRef, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ShopService } from '@app/services/shops/shop.service';
import { ExtensionsService } from '@app/pages/checkout-app/services/extensions/extensions.service';
import { GiftUnlockExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import {
  AddProductsModalComponent,
  AddProductsModalConfig,
  ProductOption,
  SelectableProduct,
} from '@app/sharedModule/product-selection/components/add-products-modal/add-products-modal.component';
import { mergeEditedProducts } from '@app/sharedModule/product-selection/utils/merge-edited-products';
import { ProductListItem } from '@storybook/components/product-list/product-list.component';
import { distinctUntilChanged, firstValueFrom } from 'rxjs';
import { BUTTON_APPEARANCE_OPTIONS, DropdownOption } from '../../../../../../shared/config/extension-ui-options';

interface GiftProduct {
  id: string | number;
  name: string;
  description?: string;
  price?: string;
  imageUrl?: string;
  sku?: string;
  priority?: number;
  selectedOptions?: ProductOption[];
}

@Component({
  selector: 'gift-unlock-offer-product',
  templateUrl: './gift-unlock-offer-product.component.html',
  styleUrls: ['./gift-unlock-offer-product.component.scss'],
  standalone: false,
})
export class GiftUnlockOfferProductComponent implements OnInit {
  @ViewChild('hideInCartLevel') hideInCartLevel: ElementRef<HTMLElement>;

  @Input() formGroup: UntypedFormGroup;
  @Input() couponTypeOptions: DropdownOption[];
  @Input() shopCurrencySymbol: string;
  @Input() giftMessageEnabled: boolean;
  @Input() isAutoApplyEnabled: boolean;

  CouponType = GiftUnlockExtension.CouponType;
  GiftMode = GiftUnlockExtension.GiftMode;
  ProductDisplayType = GiftUnlockExtension.ProductDisplayType;
  VariantSelectorType = GiftUnlockExtension.VariantSelectorType;

  readonly buttonAppearanceOptions: DropdownOption[] = BUTTON_APPEARANCE_OPTIONS;
  readonly hideInCartLevelOptions = [
    {
      value: GiftUnlockExtension.HideInCartLevel.PARENT_PRODUCT,
      label: 'Parent product only',
      description: 'Hide if any variant of the product is in cart',
    },
    {
      value: GiftUnlockExtension.HideInCartLevel.SPECIFIC_VARIANT,
      label: 'Specific variant only',
      description: 'Only hide the exact variant in cart (allow other variants)',
    },
  ];

  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private extensionsService = inject(ExtensionsService);
  private shopService = inject(ShopService);
  private savedMultipleProducts: GiftProduct[] = [];
  private previousGiftMode: GiftUnlockExtension.GiftMode | null = null;

  ngOnInit(): void {
    this.previousGiftMode = this.formGroup.get('offer.giftMode')?.value ?? null;

    this.formGroup
      .get('offer.giftMode')
      ?.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((mode: GiftUnlockExtension.GiftMode) => {
        const currentProducts: GiftProduct[] = this.formGroup.get('offer.products')?.value ?? [];
        if (mode === GiftUnlockExtension.GiftMode.SINGLE) {
          if (this.previousGiftMode === GiftUnlockExtension.GiftMode.MULTIPLE) {
            this.savedMultipleProducts = [...currentProducts];
          }
          this.formGroup.get('offer.products')?.setValue([]);
        } else if (mode === GiftUnlockExtension.GiftMode.MULTIPLE) {
          if (!currentProducts.length && this.savedMultipleProducts.length) {
            this.formGroup.get('offer.products')?.setValue(this.savedMultipleProducts);
          }
          this.savedMultipleProducts = [];
        }
        this.previousGiftMode = mode;
      });
  }

  get products(): GiftProduct[] {
    return this.formGroup?.get('offer.products')?.value ?? [];
  }

  get productsForDisplay(): ProductListItem[] {
    return this.products.map((p) => ({
      id: p.id,
      name: p.name,
      description: p.description,
      price: p.price,
      imageUrl: p.imageUrl,
      sku: p.sku,
      selectedOptions: p.selectedOptions,
    }));
  }

  get isMultipleGifts(): boolean {
    return this.formGroup?.get('offer.giftMode')?.value === GiftUnlockExtension.GiftMode.MULTIPLE;
  }

  scrollToHideInCartLevel(): void {
    this.hideInCartLevel?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  onAddProducts(): void {
    const currentProducts = this.products;
    const preSelectedProducts: SelectableProduct[] = currentProducts.map((p) => ({
      id: p.id,
      title: p.name,
      sku: p.sku,
      price: p.price,
      imageUrl: p.imageUrl,
      selectedOptions: p.selectedOptions,
    }));

    const config: AddProductsModalConfig = {
      title: currentProducts.length ? 'Edit Products' : 'Add Products',
      searchPlaceholder: 'Search products by name or SKU...',
      addButtonText: 'Select',
      enableVariants: true,
      enableEdit: true,
      allowEmptySelection: true,
      preSelectedProducts,
      canChooseOnlyOneProduct: !this.isMultipleGifts,
      hideSelectAll: true,
      openProductInShopify: (product) => this.onExternalLink(this.toGiftProduct(product)),
      fetchProducts: (query) => this.fetchProducts(query),
    };

    const dialogRef = this.dialog.open(AddProductsModalComponent, {
      data: config,
      panelClass: 'add-products-modal-container',
      width: '700px',
      maxHeight: '80vh',
    });

    dialogRef.afterClosed().subscribe((result: SelectableProduct[] | undefined) => {
      if (result) {
        const updatedProducts = this.isMultipleGifts
          ? mergeEditedProducts(
              currentProducts,
              result,
              (p) => this.toGiftProduct(p),
              (kept, selected) => ({ ...kept, selectedOptions: selected.selectedOptions }),
            )
          : result.slice(0, 1).map((p) => ({ ...this.toGiftProduct(p), priority: 0 }));
        this.formGroup.get('offer.products')?.setValue(updatedProducts);

        if (!this.isMultipleGifts && updatedProducts[0]?.selectedOptions?.length > 0) {
          this.formGroup.get('settings.productDetails.hideOfferDetails')?.setValue(false);
        }
      }
    });
  }

  onExternalLink(product: ProductListItem): void {
    const productId =
      typeof product.id === 'string' && product.id.includes('/') ? product.id.split('/').pop() : product.id;
    const shop = this.shopService.shopSignal();
    const trimmedShopName = shop.name.split('.myshopify.com')[0];
    window.open(`https://admin.shopify.com/store/${trimmedShopName}/products/${productId}`, '_blank');
  }

  private toGiftProduct(product: SelectableProduct): GiftProduct {
    return {
      id: product.id,
      name: product.title,
      description: product.description,
      imageUrl: product.imageUrl,
      sku: product.sku,
      price: product.price,
      ...(product.selectedOptions ? { selectedOptions: product.selectedOptions } : {}),
    };
  }

  private async fetchProducts(searchQuery: string): Promise<SelectableProduct[]> {
    const products = await firstValueFrom(
      this.extensionsService.queryShopifyForProductsUsingRestrictionRules({ query: searchQuery }),
    );
    return products.map((p) => (p.hasUniformPrice === false ? { ...p, price: undefined } : p));
  }
}
